/**
 * Five Pillars Overview Widget
 * A compact dashboard widget showing 5 Pillars metrics at a glance
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Target,
  Calendar,
  ClipboardList,
  ShieldCheck,
  DollarSign,
  ArrowRight,
  Loader2,
  CheckCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Pillar configuration
const PILLARS = [
  { key: 'scope', label: 'Scope', icon: Target, color: 'emerald' },
  { key: 'timeline', label: 'Timeline', icon: Calendar, color: 'blue' },
  { key: 'implementation', label: 'Implementation', icon: ClipboardList, color: 'purple' },
  { key: 'security', label: 'Data & Security', icon: ShieldCheck, color: 'amber' },
  { key: 'cost', label: 'Cost', icon: DollarSign, color: 'green' }
];

const COLOR_CLASSES = {
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', progress: 'bg-emerald-500' },
  blue: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', progress: 'bg-blue-500' },
  purple: { bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', progress: 'bg-purple-500' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', progress: 'bg-amber-500' },
  green: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', progress: 'bg-green-500' }
};

export function FivePillarsOverviewWidget({ minimized = false, embedded = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    mvpProgress: 0,
    pillars: {
      scope: { progress: 0, total: 0, completed: 0 },
      timeline: { progress: 0, total: 0, completed: 0 },
      implementation: { progress: 0, total: 0, completed: 0 },
      security: { progress: 0, total: 0, completed: 0 },
      cost: { budget: 0, spent: 0, percent: 0 }
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // Fetch all pillar data in parallel using the correct governance endpoints
        const [scopeRes, timelineRes, implRes, securityRes, costRes] = await Promise.all([
          axios.get(`${API}/api/governance/five-pillars/scope`, { headers }).catch(() => ({ data: { metrics: {} } })),
          axios.get(`${API}/api/governance/five-pillars/timeline`, { headers }).catch(() => ({ data: { milestones: [] } })),
          axios.get(`${API}/api/governance/five-pillars/implementation`, { headers }).catch(() => ({ data: { metrics: {} } })),
          axios.get(`${API}/api/governance/five-pillars/data-security`, { headers }).catch(() => ({ data: { metrics: {} } })),
          axios.get(`${API}/api/governance/five-pillars/costs`, { headers }).catch(() => ({ data: { totals: {} } }))
        ]);

        // Extract scope metrics
        const scopeMetrics = scopeRes.data?.metrics || {};
        const scopeTotal = scopeMetrics.total || 0;
        const scopeCompleted = scopeMetrics.completed || 0;
        const scopeProgress = scopeMetrics.progress_percent || (scopeTotal > 0 ? Math.round((scopeCompleted / scopeTotal) * 100) : 0);

        // Extract timeline metrics from milestones
        const milestones = timelineRes.data?.milestones || [];
        // Flatten milestones including sub-milestones
        const flatMilestones = [];
        milestones.forEach(m => {
          flatMilestones.push(m);
          if (m.sub_milestones?.length) {
            flatMilestones.push(...m.sub_milestones);
          }
        });
        const timelineTotal = flatMilestones.length;
        const timelineCompleted = flatMilestones.filter(m => m.status === 'completed').length;
        const timelineInProgress = flatMilestones.filter(m => m.status === 'in_progress').length;
        // Calculate progress based on completed + partial progress of in-progress items
        const timelineProgress = timelineTotal > 0 
          ? Math.round(((timelineCompleted + (timelineInProgress * 0.5)) / timelineTotal) * 100) 
          : 0;

        // Extract implementation metrics
        const implMetrics = implRes.data?.metrics || {};
        const implTotal = implMetrics.total || 0;
        const implCompleted = implMetrics.completed || 0;
        const implProgress = implMetrics.progress_percent || (implTotal > 0 ? Math.round((implCompleted / implTotal) * 100) : 0);

        // Extract security metrics
        const securityMetrics = securityRes.data?.metrics || {};
        const securityTotal = securityMetrics.total || 0;
        const securityReviewed = securityMetrics.reviewed || 0;
        const securityProgress = securityMetrics.review_percent || (securityTotal > 0 ? Math.round((securityReviewed / securityTotal) * 100) : 0);

        // Extract cost data
        const costTotals = costRes.data?.totals || {};
        const budget = costTotals.total_budget || 0;
        const spent = costTotals.monthly_burn || costTotals.grand_total || 0;
        const costPercent = budget > 0 ? Math.round((spent / budget) * 100) : 0;

        // Calculate overall MVP progress using scope progress (which is the main tracking metric)
        const mvpProgress = scopeMetrics.progress_percent || scopeProgress;

        setData({
          mvpProgress,
          pillars: {
            scope: { progress: scopeProgress, total: scopeTotal, completed: scopeCompleted },
            timeline: { progress: timelineProgress, total: timelineTotal, completed: timelineCompleted },
            implementation: { progress: implProgress, total: implTotal, completed: implCompleted },
            security: { progress: securityProgress, total: securityTotal, completed: securityReviewed },
            cost: { budget, spent, percent: costPercent }
          }
        });
      } catch (err) {
        console.error('Error fetching 5 pillars data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (minimized) {
    return (
      <div className="text-sm text-muted-foreground flex items-center gap-2">
        <Target className="h-4 w-4" />
        MVP Progress: {loading ? '...' : `${data.mvpProgress}%`}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-3 space-y-4 overflow-auto">
      {/* MVP Progress Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Target className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">5 Pillars Overview</h3>
            <p className="text-xs text-muted-foreground">Development progress at a glance</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-primary">{data.mvpProgress}%</div>
          <div className="text-xs text-muted-foreground">MVP Complete</div>
        </div>
      </div>

      {/* Overall Progress Bar */}
      <div className="space-y-1">
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-500 transition-all duration-500"
            style={{ width: `${data.mvpProgress}%` }}
          />
        </div>
      </div>

      {/* Pillar Cards Grid */}
      <div className="grid grid-cols-5 gap-2 flex-1">
        {PILLARS.map((pillar) => {
          const Icon = pillar.icon;
          const colors = COLOR_CLASSES[pillar.color];
          const pillarData = data.pillars[pillar.key];
          
          // Special handling for cost pillar
          const isCost = pillar.key === 'cost';
          const progress = isCost ? pillarData.percent : pillarData.progress;
          const subText = isCost 
            ? `$${(pillarData.spent / 1000).toFixed(0)}k / $${(pillarData.budget / 1000).toFixed(0)}k`
            : `${pillarData.completed}/${pillarData.total}`;

          return (
            <div 
              key={pillar.key}
              className={`${colors.bg} rounded-lg p-2 flex flex-col items-center text-center cursor-pointer hover:opacity-80 transition-opacity`}
              onClick={() => navigate('/five-pillars')}
            >
              <Icon className={`h-5 w-5 ${colors.text} mb-1`} />
              <span className="text-[10px] font-medium truncate w-full">{pillar.label}</span>
              <span className={`text-lg font-bold ${colors.text}`}>{progress}%</span>
              <span className="text-[9px] text-muted-foreground">{subText}</span>
              <div className="w-full h-1 bg-muted/50 rounded-full mt-1 overflow-hidden">
                <div 
                  className={`h-full ${colors.progress} transition-all`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Stats Row */}
      <div className="flex items-center justify-between text-xs border-t pt-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <CheckCircle className="h-3 w-3 text-green-500" />
            <span className="text-muted-foreground">
              {data.pillars.scope.completed + data.pillars.timeline.completed + data.pillars.implementation.completed} completed
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3 text-blue-500" />
            <span className="text-muted-foreground">
              {(data.pillars.scope.total - data.pillars.scope.completed) + 
               (data.pillars.timeline.total - data.pillars.timeline.completed) + 
               (data.pillars.implementation.total - data.pillars.implementation.completed)} pending
            </span>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 text-xs gap-1"
          onClick={() => navigate('/five-pillars')}
          data-testid="five-pillars-view-hub-btn"
        >
          View Full Hub
          <ArrowRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export default FivePillarsOverviewWidget;
